import { Box, InputAdornment, TextField, Typography } from '@mui/material'

import { energySourceIcons } from '@/assets/icons/energy-sources'
import { housePartsIcons } from '@/assets/icons/house-parts'
import type { RetrofitName } from '@/shared/types'
import type { CustomAssessmentInput } from '@/shared/types/custom-assessment-input.types'
import type { EnergySource } from '@/shared/types/isfp.types'
import {
  getEnergySourceIcon,
  getRetrofitDisplayName,
  retrofitToIcon,
} from '@/shared/utils/displayMappings'
import { getEnergyPriceUnitLabel, getEnergySourceLabel } from '@/shared/utils/isfpEnumLabels'

import type { useISFPFormController } from '../../hooks/useISFPFormController'
import {
  RetrofitRequiredField,
  getRetrofitRequiredField,
} from '../../utils/retrofitFieldRequirements'

interface CostAdjustmentContentProps {
  formController: ReturnType<typeof useISFPFormController>
  assessmentData: CustomAssessmentInput | null
  setAssessmentData: (data: CustomAssessmentInput) => void
}

// Helper function to format number with specified decimal places
const formatNumberWithDecimals = (value?: number | null, decimals: number = 2): number | string => {
  if (value === null || value === undefined) return ''
  return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals)
}

const CostAdjustmentContent = ({
  formController,
  assessmentData,
  setAssessmentData,
}: CostAdjustmentContentProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Energiepreise section title */}
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: '14px',
          paddingBottom: '8px',
        }}
      >
        Energiepreise
      </Typography>

      {/* Energiepreise list */}
      {(() => {
        // Combine heating generation and DHW generation
        const combinedSystems = [
          ...formController.heatingGenerationFields,
          ...formController.dhwGenerationFields,
        ]

        // Create a set starting with electricity and pellets, then add the rest
        const uniqueEnergySources = new Set<EnergySource>([
          'electricity',
          'pellets',
          ...combinedSystems.map(item => item.energySource),
        ])

        // Filter available energy sources based on unique systems
        const systemsWithDefaults = (assessmentData?.energyPrices || []).filter(price =>
          uniqueEnergySources.has(price.energySource)
        )

        if (systemsWithDefaults.length === 0) {
          return (
            <Box
              sx={{
                padding: '24px',
                backgroundColor: '#FAFAFB',
                borderRadius: '8px',
                border: '1px solid #E1E1E4',
                textAlign: 'center',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: '14px',
                  color: '#74777F',
                }}
              >
                Keine Energiepreise verfügbar
              </Typography>
            </Box>
          )
        }

        return (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '8px',
            }}
          >
            {systemsWithDefaults.map((system, index) => (
              <Box
                key={`${system.energySource}-${index}`}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  gap: '8px',
                }}
              >
                {/* Title */}
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: '11px',
                    color: '#464A54',
                  }}
                >
                  {getEnergySourceLabel(system.energySource)}
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    gap: '16px',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box
                    component="img"
                    src={energySourceIcons[getEnergySourceIcon(system.energySource)]}
                    alt={getEnergySourceLabel(system.energySource)}
                    sx={{
                      width: '56px',
                      height: '56px',
                      borderRadius: '4px',
                      objectFit: 'cover',
                    }}
                  />
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                    }}
                  >
                    <Box
                      sx={{
                        height: '58px',
                        borderRadius: '18px',
                        backgroundColor: '#F5F5F7',
                        display: 'flex',
                        alignItems: 'center',
                        paddingX: '16px',
                      }}
                    >
                      <TextField
                        label="Energiepreis"
                        type="number"
                        value={formatNumberWithDecimals(system.costs)}
                        onChange={e => {
                          const { value } = e.target
                          if (!assessmentData) return

                          // Set to null if empty
                          if (value === '') {
                            setAssessmentData({
                              ...assessmentData,
                              energyPrices: assessmentData.energyPrices.map(price =>
                                price.energySource === system.energySource
                                  ? { ...price, costs: null as unknown as number }
                                  : price
                              ),
                            })
                            return
                          }

                          const newValue = parseFloat(value)
                          if (!isNaN(newValue) && newValue >= 0) {
                            // Round to 2 decimal places
                            const roundedValue = formatNumberWithDecimals(newValue) as number
                            setAssessmentData({
                              ...assessmentData,
                              energyPrices: assessmentData.energyPrices.map(price =>
                                price.energySource === system.energySource
                                  ? { ...price, costs: roundedValue }
                                  : price
                              ),
                            })
                          }
                        }}
                        onKeyDown={e => {
                          if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                            e.preventDefault()
                          }
                        }}
                        variant="standard"
                        fullWidth
                        error={!system.costs || system.costs <= 0}
                        slotProps={{
                          inputLabel: { shrink: true },
                          input: {
                            inputProps: { step: 0.1 },
                            endAdornment: (
                              <InputAdornment position="end">
                                <Typography
                                  sx={{
                                    color: '#464A54',
                                    fontWeight: 500,
                                    fontSize: '16px',
                                  }}
                                >
                                  {getEnergyPriceUnitLabel(system.unit)}
                                </Typography>
                              </InputAdornment>
                            ),
                          },
                        }}
                        sx={{
                          '& .MuiInput-root::before': {
                            borderBottom: 'none',
                          },
                          '& .MuiInput-root::after': {
                            borderBottom: 'none',
                          },
                          '& .MuiInput-root:hover::before': {
                            borderBottom: 'none !important',
                          },
                          '& .MuiInput-root:hover::after': {
                            borderBottom: 'none !important',
                          },
                          '& .MuiInput-root:hover:not(.Mui-disabled, .Mui-error)::before': {
                            borderBottom: 'none !important',
                          },
                          '& .MuiInputLabel-root': {
                            color: '#74777F',
                            fontWeight: 500,
                            fontSize: '13px',
                          },
                          '& .MuiInputBase-input': {
                            fontWeight: 500,
                            fontSize: '16px',
                            paddingY: 0,
                          },
                          '& input[type=number]::-webkit-outer-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0,
                          },
                          '& input[type=number]::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0,
                          },
                          '& input[type=number]': {
                            MozAppearance: 'textfield',
                          },
                        }}
                      />
                    </Box>
                    {(!system.costs || system.costs <= 0) && (
                      <Typography
                        sx={{
                          color: '#d32f2f',
                          fontSize: '12px',
                          fontWeight: 400,
                          paddingLeft: '16px',
                        }}
                      >
                        Bitte geben Sie einen Wert ein
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )
      })()}

      {/* Divider */}
      <Box
        sx={{
          height: '1px',
          backgroundColor: '#E1E1E4',
          marginY: '16px',
        }}
      />

      {/* Sanierungen section title */}
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: '14px',
          paddingBottom: '8px',
        }}
      >
        Sanierungen
      </Typography>

      {/* Sanierungen list */}
      {(() => {
        const retrofits = assessmentData?.retrofits || []

        // Map retrofit names to substance field arrays
        const retrofitToSubstanceMap: Record<RetrofitName, unknown[]> = {
          exteriorWallETICS: formController.exteriorWallFields,
          windows: formController.windowFields,
          roofWindows: formController.roofWindowFields,
          door: formController.doorFields,
          basementExteriorWall: formController.basementExteriorWallFields,
          basementCeiling: formController.basementCeilingFields,
          roof: formController.roofFields,
          topFloorCeiling: formController.topFloorCeilingFields,
          floorPlate: formController.floorPlateFields,
          blowInInsulation: formController.exteriorWallFields,
          // always shown
          groundSourceHeatPump: [],
          airWaterHeatPump: [],
          airAirHeatPump: [],
          pelletHeating: [],
          districtHeating: [],
          decentralVentilation: [],
        }

        // Filter retrofits that have corresponding substance items
        // Always include power-based retrofits (heating systems)
        const retrofitsWithSubstance = retrofits.filter(retrofit => {
          const alwaysInclude = ['districtHeating', 'decentralVentilation']
          if (alwaysInclude.includes(retrofit.retrofitName)) return true

          const requiredField = getRetrofitRequiredField(retrofit.retrofitName)
          if (requiredField === RetrofitRequiredField.POWER) return true

          const substanceFields = retrofitToSubstanceMap[retrofit.retrofitName]
          return substanceFields && substanceFields.length > 0
        })

        if (retrofitsWithSubstance.length === 0) {
          return (
            <Box
              sx={{
                padding: '24px',
                backgroundColor: '#FAFAFB',
                borderRadius: '8px',
                border: '1px solid #E1E1E4',
                textAlign: 'center',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: '14px',
                  color: '#74777F',
                }}
              >
                Keine Sanierungen verfügbar
              </Typography>
            </Box>
          )
        }

        return (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(391px, 1fr))',
              rowGap: '32px',
              columnGap: '16px',
            }}
          >
            {retrofitsWithSubstance.map((retrofit, index) => {
              const requiredField = getRetrofitRequiredField(retrofit.retrofitName)
              const isPowerBased = requiredField === RetrofitRequiredField.POWER
              const isAreaBased = requiredField === RetrofitRequiredField.AREA

              // Determine which textfields to display and calculate layout
              // Field 1: Dämmfläche (area-based) or Heizlast (power-based) - shown for most retrofits
              const hasAreaOrPowerField = requiredField !== RetrofitRequiredField.NONE
              // Field 2: Dämmstärke - only shown for blowInInsulation
              const hasInsulationThicknessField = retrofit.retrofitName === 'blowInInsulation'
              // Field 3: Gesamtkosten - always shown
              // Total count determines layout: 1 = full width, 2 = flex(130,173), 3 = two rows
              const totalTextfieldCount =
                (hasAreaOrPowerField ? 1 : 0) + (hasInsulationThicknessField ? 1 : 0) + 1

              return (
                <Box
                  key={`retrofit-${index}`}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                  }}
                >
                  {/* Title */}
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: '11px',
                      color: '#464A54',
                    }}
                  >
                    {getRetrofitDisplayName(retrofit.retrofitName)}
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      gap: '16px',
                      alignItems: 'flex-start',
                    }}
                  >
                    {/* Retrofit Icon */}
                    <Box
                      component="img"
                      src={housePartsIcons[retrofitToIcon[retrofit.retrofitName]]}
                      alt={getRetrofitDisplayName(retrofit.retrofitName)}
                      sx={{
                        width: '56px',
                        height: '56px',
                        objectFit: 'contain',
                      }}
                    />

                    {/* Textfields Container */}
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: totalTextfieldCount === 3 ? 'column' : 'row',
                        gap: '16px',
                      }}
                    >
                      {/* First row for 3 textfields, or only row for 1-2 textfields */}
                      <Box
                        sx={{
                          display: 'flex',
                          gap: '16px',
                          flex: totalTextfieldCount === 3 ? undefined : 1,
                        }}
                      >
                        {/* Dämmfläche/Heizlast TextField */}
                        {hasAreaOrPowerField && (
                          <Box
                            sx={{
                              flex: 130,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '4px',
                            }}
                          >
                            <Box
                              sx={{
                                height: '58px',
                                borderRadius: '18px',
                                backgroundColor: '#F5F5F7',
                                display: 'flex',
                                alignItems: 'center',
                                paddingX: '16px',
                              }}
                            >
                              <TextField
                                label={isPowerBased ? 'Heizlast' : 'Dämmfläche'}
                                type="number"
                                value={formatNumberWithDecimals(
                                  isPowerBased ? retrofit.powerInKw : retrofit.componentAreaInSqm
                                )}
                                onChange={e => {
                                  const { value } = e.target
                                  if (!assessmentData) return

                                  const fieldName = isPowerBased
                                    ? 'powerInKw'
                                    : 'componentAreaInSqm'

                                  // Set to null if empty
                                  if (value === '') {
                                    setAssessmentData({
                                      ...assessmentData,
                                      retrofits: assessmentData.retrofits.map(r =>
                                        r.retrofitName === retrofit.retrofitName
                                          ? { ...r, [fieldName]: undefined }
                                          : r
                                      ),
                                    })
                                    return
                                  }

                                  const newValue = parseFloat(value)
                                  if (!isNaN(newValue) && newValue >= 0) {
                                    // Round to 2 decimal places
                                    const roundedValue = formatNumberWithDecimals(
                                      newValue
                                    ) as number
                                    setAssessmentData({
                                      ...assessmentData,
                                      retrofits: assessmentData.retrofits.map(r =>
                                        r.retrofitName === retrofit.retrofitName
                                          ? { ...r, [fieldName]: roundedValue }
                                          : r
                                      ),
                                    })
                                  }
                                }}
                                onKeyDown={e => {
                                  if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                                    e.preventDefault()
                                  }
                                }}
                                variant="standard"
                                fullWidth
                                error={
                                  isPowerBased
                                    ? (retrofit.powerInKw ?? 0) < 0
                                    : (retrofit.componentAreaInSqm ?? 0) < 0
                                }
                                slotProps={{
                                  inputLabel: { shrink: true },
                                  input: {
                                    inputProps: { step: 0.01 },
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <Typography
                                          sx={{
                                            color: '#464A54',
                                            fontWeight: 500,
                                            fontSize: '16px',
                                          }}
                                        >
                                          {isPowerBased ? 'kW' : 'm²'}
                                        </Typography>
                                      </InputAdornment>
                                    ),
                                  },
                                }}
                                sx={{
                                  '& .MuiInput-root::before': {
                                    borderBottom: 'none',
                                  },
                                  '& .MuiInput-root::after': {
                                    borderBottom: 'none',
                                  },
                                  '& .MuiInput-root:hover::before': {
                                    borderBottom: 'none !important',
                                  },
                                  '& .MuiInput-root:hover::after': {
                                    borderBottom: 'none !important',
                                  },
                                  '& .MuiInput-root:hover:not(.Mui-disabled, .Mui-error)::before': {
                                    borderBottom: 'none !important',
                                  },
                                  '& .MuiInputLabel-root': {
                                    color: '#74777F',
                                    fontWeight: 500,
                                    fontSize: '13px',
                                  },
                                  '& .MuiInputBase-input': {
                                    fontWeight: 500,
                                    fontSize: '16px',
                                    paddingY: 0,
                                  },
                                  '& input[type=number]::-webkit-outer-spin-button': {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                  },
                                  '& input[type=number]::-webkit-inner-spin-button': {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                  },
                                  '& input[type=number]': {
                                    MozAppearance: 'textfield',
                                  },
                                }}
                              />
                            </Box>
                            {((isPowerBased && (retrofit.powerInKw ?? 0) < 0) ||
                              (isAreaBased && (retrofit.componentAreaInSqm ?? 0) < 0)) && (
                              <Typography
                                sx={{
                                  color: '#d32f2f',
                                  fontSize: '12px',
                                  fontWeight: 400,
                                  paddingLeft: '16px',
                                }}
                              >
                                Bitte geben Sie einen Wert ein
                              </Typography>
                            )}
                          </Box>
                        )}

                        {/* Dämmstärke TextField - only for blowInInsulation */}
                        {hasInsulationThicknessField && (
                          <Box
                            sx={{
                              flex: 130,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '4px',
                            }}
                          >
                            <Box
                              sx={{
                                height: '58px',
                                borderRadius: '18px',
                                backgroundColor: '#F5F5F7',
                                display: 'flex',
                                alignItems: 'center',
                                paddingX: '16px',
                              }}
                            >
                              <TextField
                                label="Dämmstärke"
                                type="number"
                                value={retrofit.insulationThicknessInMm ?? ''}
                                onChange={e => {
                                  const { value } = e.target
                                  if (!assessmentData) return

                                  // Set to undefined if empty
                                  if (value === '') {
                                    setAssessmentData({
                                      ...assessmentData,
                                      retrofits: assessmentData.retrofits.map(r =>
                                        r.retrofitName === retrofit.retrofitName
                                          ? { ...r, insulationThicknessInMm: undefined }
                                          : r
                                      ),
                                    })
                                    return
                                  }

                                  const newValue = parseInt(value, 10)
                                  if (!isNaN(newValue) && newValue >= 0) {
                                    setAssessmentData({
                                      ...assessmentData,
                                      retrofits: assessmentData.retrofits.map(r =>
                                        r.retrofitName === retrofit.retrofitName
                                          ? { ...r, insulationThicknessInMm: newValue }
                                          : r
                                      ),
                                    })
                                  }
                                }}
                                onKeyDown={e => {
                                  if (
                                    e.key === '-' ||
                                    e.key === 'e' ||
                                    e.key === 'E' ||
                                    e.key === '.' ||
                                    e.key === ','
                                  ) {
                                    e.preventDefault()
                                  }
                                }}
                                variant="standard"
                                fullWidth
                                error={(retrofit.insulationThicknessInMm ?? 0) < 0}
                                slotProps={{
                                  inputLabel: { shrink: true },
                                  input: {
                                    inputProps: { step: 1 },
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <Typography
                                          sx={{
                                            color: '#464A54',
                                            fontWeight: 500,
                                            fontSize: '16px',
                                          }}
                                        >
                                          mm
                                        </Typography>
                                      </InputAdornment>
                                    ),
                                  },
                                }}
                                sx={{
                                  '& .MuiInput-root::before': {
                                    borderBottom: 'none',
                                  },
                                  '& .MuiInput-root::after': {
                                    borderBottom: 'none',
                                  },
                                  '& .MuiInput-root:hover::before': {
                                    borderBottom: 'none !important',
                                  },
                                  '& .MuiInput-root:hover::after': {
                                    borderBottom: 'none !important',
                                  },
                                  '& .MuiInput-root:hover:not(.Mui-disabled, .Mui-error)::before': {
                                    borderBottom: 'none !important',
                                  },
                                  '& .MuiInputLabel-root': {
                                    color: '#74777F',
                                    fontWeight: 500,
                                    fontSize: '13px',
                                  },
                                  '& .MuiInputBase-input': {
                                    fontWeight: 500,
                                    fontSize: '16px',
                                    paddingY: 0,
                                  },
                                  '& input[type=number]::-webkit-outer-spin-button': {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                  },
                                  '& input[type=number]::-webkit-inner-spin-button': {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                  },
                                  '& input[type=number]': {
                                    MozAppearance: 'textfield',
                                  },
                                }}
                              />
                            </Box>
                            {(retrofit.insulationThicknessInMm ?? 0) < 0 && (
                              <Typography
                                sx={{
                                  color: '#d32f2f',
                                  fontSize: '12px',
                                  fontWeight: 400,
                                  paddingLeft: '16px',
                                }}
                              >
                                Bitte geben Sie einen Wert ein
                              </Typography>
                            )}
                          </Box>
                        )}

                        {/* Gesamtkosten TextField - conditionally in first or second row */}
                        {totalTextfieldCount !== 3 && (
                          <Box
                            sx={{
                              flex: totalTextfieldCount === 1 ? 1 : 173,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '4px',
                            }}
                          >
                            <Box
                              sx={{
                                height: '58px',
                                borderRadius: '18px',
                                backgroundColor: '#F5F5F7',
                                display: 'flex',
                                alignItems: 'center',
                                paddingX: '16px',
                              }}
                            >
                              <TextField
                                label="Gesamtkosten"
                                type="number"
                                value={retrofit.totalCostsInCt ? retrofit.totalCostsInCt / 100 : ''}
                                onChange={e => {
                                  const { value } = e.target
                                  if (!assessmentData) return

                                  // Set to null if empty
                                  if (value === '') {
                                    setAssessmentData({
                                      ...assessmentData,
                                      retrofits: assessmentData.retrofits.map(r =>
                                        r.retrofitName === retrofit.retrofitName
                                          ? { ...r, totalCostsInCt: null as unknown as number }
                                          : r
                                      ),
                                    })
                                    return
                                  }

                                  const newValue = parseFloat(value)
                                  if (!isNaN(newValue) && newValue >= 0) {
                                    setAssessmentData({
                                      ...assessmentData,
                                      retrofits: assessmentData.retrofits.map(r =>
                                        r.retrofitName === retrofit.retrofitName
                                          ? { ...r, totalCostsInCt: Math.round(newValue * 100) }
                                          : r
                                      ),
                                    })
                                  }
                                }}
                                onKeyDown={e => {
                                  if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                                    e.preventDefault()
                                  }
                                }}
                                variant="standard"
                                fullWidth
                                error={(retrofit.totalCostsInCt ?? 0) < 0}
                                slotProps={{
                                  inputLabel: { shrink: true },
                                  input: {
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <Typography
                                          sx={{
                                            color: '#464A54',
                                            fontWeight: 500,
                                            fontSize: '16px',
                                          }}
                                        >
                                          €
                                        </Typography>
                                      </InputAdornment>
                                    ),
                                  },
                                }}
                                sx={{
                                  '& .MuiInput-root::before': {
                                    borderBottom: 'none',
                                  },
                                  '& .MuiInput-root::after': {
                                    borderBottom: 'none',
                                  },
                                  '& .MuiInput-root:hover::before': {
                                    borderBottom: 'none !important',
                                  },
                                  '& .MuiInput-root:hover::after': {
                                    borderBottom: 'none !important',
                                  },
                                  '& .MuiInput-root:hover:not(.Mui-disabled, .Mui-error)::before': {
                                    borderBottom: 'none !important',
                                  },
                                  '& .MuiInputLabel-root': {
                                    color: '#74777F',
                                    fontWeight: 500,
                                    fontSize: '13px',
                                  },
                                  '& .MuiInputBase-input': {
                                    fontWeight: 500,
                                    fontSize: '16px',
                                    paddingY: 0,
                                  },
                                  '& input[type=number]::-webkit-outer-spin-button': {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                  },
                                  '& input[type=number]::-webkit-inner-spin-button': {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                  },
                                  '& input[type=number]': {
                                    MozAppearance: 'textfield',
                                  },
                                }}
                              />
                            </Box>
                            {(retrofit.totalCostsInCt ?? 0) < 0 && (
                              <Typography
                                sx={{
                                  color: '#d32f2f',
                                  fontSize: '12px',
                                  fontWeight: 400,
                                  paddingLeft: '16px',
                                }}
                              >
                                Bitte geben Sie einen Wert ein
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>

                      {/* Second row - Gesamtkosten for 3 textfields case */}
                      {totalTextfieldCount === 3 && (
                        <Box
                          sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                          }}
                        >
                          <Box
                            sx={{
                              height: '58px',
                              borderRadius: '18px',
                              backgroundColor: '#F5F5F7',
                              display: 'flex',
                              alignItems: 'center',
                              paddingX: '16px',
                            }}
                          >
                            <TextField
                              label="Gesamtkosten"
                              type="number"
                              value={retrofit.totalCostsInCt ? retrofit.totalCostsInCt / 100 : ''}
                              onChange={e => {
                                const { value } = e.target
                                if (!assessmentData) return

                                // Set to null if empty
                                if (value === '') {
                                  setAssessmentData({
                                    ...assessmentData,
                                    retrofits: assessmentData.retrofits.map(r =>
                                      r.retrofitName === retrofit.retrofitName
                                        ? { ...r, totalCostsInCt: null as unknown as number }
                                        : r
                                    ),
                                  })
                                  return
                                }

                                const newValue = parseFloat(value)
                                if (!isNaN(newValue) && newValue >= 0) {
                                  setAssessmentData({
                                    ...assessmentData,
                                    retrofits: assessmentData.retrofits.map(r =>
                                      r.retrofitName === retrofit.retrofitName
                                        ? { ...r, totalCostsInCt: Math.round(newValue * 100) }
                                        : r
                                    ),
                                  })
                                }
                              }}
                              onKeyDown={e => {
                                if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                                  e.preventDefault()
                                }
                              }}
                              variant="standard"
                              fullWidth
                              error={(retrofit.totalCostsInCt ?? 0) < 0}
                              slotProps={{
                                inputLabel: { shrink: true },
                                input: {
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <Typography
                                        sx={{
                                          color: '#464A54',
                                          fontWeight: 500,
                                          fontSize: '16px',
                                        }}
                                      >
                                        €
                                      </Typography>
                                    </InputAdornment>
                                  ),
                                },
                              }}
                              sx={{
                                '& .MuiInput-root::before': {
                                  borderBottom: 'none',
                                },
                                '& .MuiInput-root::after': {
                                  borderBottom: 'none',
                                },
                                '& .MuiInput-root:hover::before': {
                                  borderBottom: 'none !important',
                                },
                                '& .MuiInput-root:hover::after': {
                                  borderBottom: 'none !important',
                                },
                                '& .MuiInput-root:hover:not(.Mui-disabled, .Mui-error)::before': {
                                  borderBottom: 'none !important',
                                },
                                '& .MuiInputLabel-root': {
                                  color: '#74777F',
                                  fontWeight: 500,
                                  fontSize: '13px',
                                },
                                '& .MuiInputBase-input': {
                                  fontWeight: 500,
                                  fontSize: '16px',
                                  paddingY: 0,
                                },
                                '& input[type=number]::-webkit-outer-spin-button': {
                                  WebkitAppearance: 'none',
                                  margin: 0,
                                },
                                '& input[type=number]::-webkit-inner-spin-button': {
                                  WebkitAppearance: 'none',
                                  margin: 0,
                                },
                                '& input[type=number]': {
                                  MozAppearance: 'textfield',
                                },
                              }}
                            />
                          </Box>
                          {(retrofit.totalCostsInCt ?? 0) < 0 && (
                            <Typography
                              sx={{
                                color: '#d32f2f',
                                fontSize: '12px',
                                fontWeight: 400,
                                paddingLeft: '16px',
                              }}
                            >
                              Bitte geben Sie einen Wert ein
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              )
            })}
          </Box>
        )
      })()}
    </Box>
  )
}

export default CostAdjustmentContent
